import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Top-of-page progress bar that animates while a route transition is happening.
 *
 * React Router v6 + React.lazy keeps the previous page on screen while the new
 * route's chunk downloads. Without feedback this looks like the app is frozen.
 * This bar starts the moment a same-origin internal link is clicked and finishes
 * once the URL actually changes.
 */
export const RouteProgressBar = () => {
  const { pathname } = useLocation();
  const [progress, setProgress] = useState(0); // 0..100
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<number | null>(null);
  const lastPathRef = useRef(pathname);

  // Listen for clicks on internal links to start the bar BEFORE pathname changes.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const target = (e.target as HTMLElement | null)?.closest?.("a");
      if (!target) return;
      const anchor = target as HTMLAnchorElement;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href");
      if (!href) return;
      // Only intercept internal navigations
      if (href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      if (href.startsWith("#")) return;

      // Same-page: no transition
      try {
        const url = new URL(anchor.href, window.location.href);
        if (url.origin !== window.location.origin) return;
        if (url.pathname === window.location.pathname) return;
      } catch {
        return;
      }

      start();
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the pathname finally updates, finish the bar.
  useEffect(() => {
    if (lastPathRef.current !== pathname) {
      lastPathRef.current = pathname;
      finish();
    }
  }, [pathname]);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const start = () => {
    clearTimer();
    setVisible(true);
    setProgress(8);
    // Trickle up to ~85% asymptotically so users always see motion
    timerRef.current = window.setInterval(() => {
      setProgress((p) => {
        if (p >= 85) return p;
        const remaining = 90 - p;
        return Math.min(85, p + Math.max(1, remaining * 0.08));
      });
    }, 120);
  };

  const finish = () => {
    clearTimer();
    setProgress(100);
    window.setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 250);
  };

  return (
    <div
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 z-[100] h-[2px] pointer-events-none"
      style={{ opacity: visible ? 1 : 0, transition: "opacity 200ms" }}
    >
      <div
        className="h-full bg-accent shadow-[0_0_8px_hsl(var(--accent)/0.6)]"
        style={{
          width: `${progress}%`,
          transition: "width 180ms ease-out",
        }}
      />
    </div>
  );
};
